import React, { useState } from 'react';
import Icon from '../../../AppIcon';
import Image from '../../../AppImage';

const FeaturesSection = () => {
  const [activeFeature, setActiveFeature] = useState(0);

  const features = [
  {
    id: 'intelligent-matching',
    title: 'AI-Powered Intelligent Matching',
    description: 'Advanced algorithms analyze corporate needs and startup capabilities to create perfect partnerships with 94.2% success rate.',
    image: "https://images.unsplash.com/photo-1573311525852-81c1a0b8d03c",
    imageAlt: 'Modern data visualization dashboard showing AI matching algorithms with colorful charts and graphs',
    icon: 'Brain',
    benefits: [
    'Machine learning optimization',
    'Real-time compatibility scoring',
    'Predictive partnership success',
    'Automated quality filtering']

  },
  {
    id: 'real-time-verification',
    title: 'Real-Time Field Verification',
    description: 'Mobile-first verification system with offline capabilities ensures startup authenticity through field-validated data collection.',
    image: "https://images.unsplash.com/photo-1735469157670-1212e570eadc",
    imageAlt: 'Business professional using mobile device for data verification in modern office environment',
    icon: 'ShieldCheck',
    benefits: [
    'Mobile verification tools',
    'Offline data collection',
    'Multi-stage validation',
    'Real-time status updates']

  },
  {
    id: 'performance-analytics',
    title: 'Performance Analytics Suite',
    description: 'Comprehensive tracking and analytics for referral performance, ROI measurement, and strategic decision-making insights.',
    image: "https://images.unsplash.com/photo-1608222351212-18fe0ec7b13b",
    imageAlt: 'Financial analytics dashboard with performance charts, ROI metrics, and business intelligence graphs',
    icon: 'BarChart3',
    benefits: [
    'ROI tracking and reporting',
    'Performance benchmarking',
    'Predictive analytics',
    'Custom dashboard creation']

  },
  {
    id: 'ecosystem-network',
    title: 'Global Ecosystem Network',
    description: 'Connect with 29,200+ verified participants across 85+ countries in the world\'s largest innovation matching platform.',
    image: "https://images.unsplash.com/photo-1703757117920-13ff79115e45",
    imageAlt: 'Global network visualization showing connected nodes across world map with glowing connection lines',
    icon: 'Globe',
    benefits: [
    '85+ country coverage',
    'Verified participant network',
    'Cross-border partnerships',
    'Cultural compatibility matching']

  }];


  const capabilities = [
  {
    icon: 'Zap',
    title: 'Instant Matching',
    description: 'Real-time opportunity identification and notification system'
  },
  {
    icon: 'Shield',
    title: 'Trust & Security',
    description: 'SOC 2 certified platform with enterprise-grade security'
  },
  {
    icon: 'TrendingUp',
    title: 'Growth Tracking',
    description: 'Monitor partnership success and revenue generation'
  },
  {
    icon: 'Users',
    title: 'Network Effects',
    description: 'Leverage collective intelligence and network growth'
  },
  {
    icon: 'Clock',
    title: '24/7 Operations',
    description: 'Continuous matching and verification processes'
  },
  {
    icon: 'Award',
    title: 'Quality Assurance',
    description: 'Multi-stage verification and quality control systems'
  }];


  return (
    <section className="py-20 bg-gradient-to-b from-white to-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="Sparkles" size={16} />
            <span>Platform Capabilities</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
            Innovation Ecosystem Redefined
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            ScoutBridge combines cutting-edge technology with human expertise to create the most efficient startup-corporate matching platform in the market.
          </p>
        </div>

        {/* Main Features */}
        <div className="mb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Feature Navigation */}
            <div className="space-y-6">
              {features?.map((feature, index) =>
              <div
                key={feature?.id}
                className={`p-6 rounded-xl cursor-pointer transition-all duration-300 ${
                activeFeature === index ?
                'bg-primary text-primary-foreground shadow-lg' :
                'bg-white hover:bg-slate-50 border border-slate-200'}`
                }
                onClick={() => setActiveFeature(index)}>

                  <div className="flex items-start space-x-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  activeFeature === index ? 'bg-white/20' : 'bg-primary/10'}`
                  }>
                      <Icon
                      name={feature?.icon}
                      size={24}
                      className={activeFeature === index ? 'text-white' : 'text-primary'} />

                    </div>
                    <div className="flex-1">
                      <h3 className={`text-xl font-semibold mb-2 ${
                    activeFeature === index ? 'text-white' : 'text-slate-900'}`
                    }>
                        {feature?.title}
                      </h3>
                      <p className={`text-sm leading-relaxed ${
                    activeFeature === index ? 'text-white/90' : 'text-slate-600'}`
                    }>
                        {feature?.description}
                      </p>
                      
                      {/* Benefits List */}
                      <div className="mt-4 space-y-2">
                        {feature?.benefits?.map((benefit, benefitIndex) =>
                      <div key={benefitIndex} className="flex items-center space-x-2">
                            <Icon
                          name="Check"
                          size={14}
                          className={activeFeature === index ? 'text-white' : 'text-emerald-500'} />

                            <span className={`text-sm ${
                        activeFeature === index ? 'text-white/90' : 'text-slate-600'}`
                        }>
                              {benefit}
                            </span>
                          </div>
                      )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Feature Image */}
            <div className="relative">
              <div className="relative h-96 lg:h-[500px] rounded-2xl overflow-hidden shadow-2xl">
                <Image
                  src={features?.[activeFeature]?.image}
                  alt={features?.[activeFeature]?.imageAlt}
                  className="w-full h-full object-cover" />

                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                
                {/* Feature Badge */}
                <div className="absolute top-6 left-6 bg-white/90 backdrop-blur-sm rounded-lg px-4 py-2">
                  <div className="flex items-center space-x-2">
                    <Icon name={features?.[activeFeature]?.icon} size={20} className="text-primary" />
                    <span className="font-medium text-slate-900">{features?.[activeFeature]?.title}</span>
                  </div>
                </div>

                {/* Stats Overlay */}
                <div className="absolute bottom-6 right-6 bg-white/90 backdrop-blur-sm rounded-lg p-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-slate-900">94.2%</div>
                    <div className="text-sm text-slate-600">Success Rate</div>
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-primary to-accent rounded-full opacity-20 animate-float"></div>
              <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-gradient-to-br from-emerald-500 to-blue-500 rounded-full opacity-20 animate-float" style={{ animationDelay: '1s' }}></div>
            </div>
          </div>
        </div>

        {/* Capabilities Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {capabilities?.map((capability, index) =>
          <div
            key={index}
            className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-100 group">

              <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-accent/10 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-200">
                <Icon name={capability?.icon} size={24} className="text-primary" />
              </div>
              
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                {capability?.title}
              </h3>
              
              <p className="text-sm text-slate-600 leading-relaxed">
                {capability?.description}
              </p>
            </div>
          )}
        </div>

        {/* Integration Showcase */}
        <div className="mt-20 bg-gradient-to-r from-slate-900 to-blue-900 rounded-2xl p-8 lg:p-12 text-white">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl lg:text-3xl font-bold mb-4">
                Seamless Integration Ecosystem
              </h3>
              <p className="text-lg text-white/90 mb-6">
                Connect with your existing tools and workflows through our comprehensive API and integration platform.
              </p>
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                {[
                { name: 'CRM Systems', icon: 'Database' },
                { name: 'Analytics Tools', icon: 'BarChart3' },
                { name: 'Communication', icon: 'MessageSquare' },
                { name: 'Project Management', icon: 'Kanban' }]?.
                map((integration, index) =>
                <div key={index} className="flex items-center space-x-2">
                    <Icon name={integration?.icon} size={16} className="text-white/70" />
                    <span className="text-sm text-white/90">{integration?.name}</span>
                  </div>
                )}
              </div>
              
              <div className="flex items-center space-x-4">
                <button className="bg-white text-slate-900 px-6 py-3 rounded-lg font-medium hover:bg-white/90 transition-colors duration-200">
                  View Integrations
                </button>
                <button className="text-white border border-white/30 px-6 py-3 rounded-lg font-medium hover:bg-white/10 transition-colors duration-200">
                  API Documentation
                </button>
              </div>
            </div>
            
            <div className="relative">
              <div className="grid grid-cols-3 gap-4">
                {[1, 2, 3, 4, 5, 6]?.map((item) =>
                <div
                  key={item}
                  className="w-16 h-16 bg-white/10 rounded-lg flex items-center justify-center animate-pulse"
                  style={{ animationDelay: `${item * 0.2}s` }}>

                    <div className="w-8 h-8 bg-white/20 rounded"></div>
                  </div>
                )}
              </div>
              
              {/* Connection Lines */}
              <div className="absolute inset-0 pointer-events-none">
                <svg className="w-full h-full" viewBox="0 0 200 200">
                  <defs>
                    <linearGradient id="connectionGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="rgba(255,255,255,0.3)" />
                      <stop offset="100%" stopColor="rgba(255,255,255,0.1)" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M50,50 Q100,25 150,50 Q175,100 150,150 Q100,175 50,150 Q25,100 50,50"
                    fill="none"
                    stroke="url(#connectionGradient)"
                    strokeWidth="2"
                    className="animate-pulse" />

                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default FeaturesSection;